<template>
  <div>
    <div class="mb-2 d-flex align-items-center gap-2">
      <router-link
        :to="{ name: 'cost-prices-unit-prices' }"
        class="d-flex align-items-center gap-2"
      >
        <feather-icon icon="ArrowLeftIcon" size="15" class="mr-1" />
        {{ t('Back to List') }}
      </router-link>
    </div>
    <b-overlay
      variant="white"
      :show="submitting"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <validation-observer ref="shipmentInfoForm">
        <b-form ref="shipmentInfoFormElement" class="" @submit.prevent="preview">
          <b-row>
            <!-- Provider -->
            <b-col sm="12" md="12">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="provider">{{ t('Provider') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider #default="{ errors }" :name="t('Provider')" rules="required">
                  <v-select
                    id="provider"
                    v-model="values.providerId"
                    :options="providers"
                    :placeholder="t('Make a Choice')"
                    label="text"
                    :tabindex="2"
                  >
                    <span slot="no-options">{{ t('No records found') }}</span>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Taşıma Yönü (Transport Direction) -->
            <b-col sm="12" md="12">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="transport_direction">{{ t('Transport Direction') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Transport Direction')"
                  rules="required"
                >
                  <v-select
                    id="transport_direction"
                    v-model="values.transportDirection"
                    :options="transportDirectionOptions"
                    :placeholder="t('Make a Choice')"
                    label="text"
                    :tabindex="3"
                  >
                    <span slot="no-options">{{ t('No records found') }}</span>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Taşıma Tipi (Transport Type) -->
            <b-col sm="12" md="12">
              <b-form-group v-if="showTransportType">
                <div class="d-flex form-group-label">
                  <label for="transport_type">{{ t('Transport Type') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Transport Type')"
                  rules="required"
                >
                  <v-select
                    id="transport_type"
                    v-model="values.transportType"
                    :options="transportTypeOptions"
                    :placeholder="t('Make a Choice')"
                    label="text"
                    :tabindex="4"
                  >
                    <span slot="no-options">{{ t('No records found') }}</span>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Paketleme Tipi (Packaging Type) -->
            <b-col sm="12" md="6">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="packaging_type">{{ t('Packaging Type') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Packaging Type')"
                  rules="required"
                >
                  <v-select
                    id="packaging_type"
                    v-model="values.packagingType"
                    :options="packagingTypes"
                    :placeholder="t('Make a Choice')"
                    label="text"
                    :tabindex="5"
                  >
                    <span slot="no-options">{{ t('No records found') }}</span>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Paket Tipi (Package Type) -->
            <b-col sm="12" md="6">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="package_type">{{ t('Package Type') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Package Type')"
                  :rules="
                    values.packagingType && values.packagingType.value !== 3 ? 'required' : ''
                  "
                >
                  <v-select
                    :disabled="values.packagingType && values.packagingType.value === 3"
                    id="package_type"
                    v-model="values.packageTypeValue"
                    :options="packageTypes"
                    :placeholder="t('Make a Choice')"
                    label="text"
                    :tabindex="6"
                  >
                    <span slot="no-options">{{ t('No records found') }}</span>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Geçerlilik Tarihi (Validity Date Range) -->
            <b-col sm="12" md="12">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label>{{ t('Validity Date') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Validity Date')"
                  rules="required"
                >
                  <b-row>
                    <b-col sm="12" md="6">
                      <date-picker
                        v-model="values.activeAt"
                        :placeholder="t('Start Date')"
                        :config="{
                          enableTime: true
                        }"
                        :tabindex="7"
                      />
                    </b-col>
                    <b-col sm="12" md="6">
                      <date-picker
                        v-model="values.activeUntil"
                        :placeholder="t('End Date')"
                        :config="{
                          enableTime: true
                        }"
                        :tabindex="8"
                      />
                    </b-col>
                  </b-row>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Download Template Link -->
            <b-col cols="12" class="mb-1" v-if="values.providerId && values.providerId.value">
              <a class="download_xls" data="1" @click="downloadTemplate" href="#">
                {{ $t('Download Cost Table Template') }} ({{ values.providerId.text }})
              </a>
            </b-col>

            <!-- File Upload -->
            <b-col cols="12">
              <validation-provider
                #default="{ errors, validate }"
                :name="t('File')"
                rules="required"
                vid="file"
              >
                <drop-zone @filesSelected="handleFileSelect($event) || validate($event)" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>

            <!-- Submit Button -->
            <b-col cols="12">
              <div
                class="d-flex gap-3 align-items-center justify-content-end mt-1"
                style="gap: 12px"
              >
                <b-button
                  variant="primary"
                  class="d-flex align-items-center"
                  type="submit"
                  :tabindex="9"
                >
                  {{ t('Preview and Save') }}
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>

    <b-modal
      size="xl"
      id="pricingPreview"
      @close="previewData = null"
      cancel-variant="outline-secondary"
      :ok-title="t('Confirm')"
      :cancel-title="t('Close')"
      centered
      :title="t('Pricing Preview')"
      @ok="submit"
    >
      <b-overlay rounded="md" variant="transparent">
        <div class="mb-1 font-bold">
          {{ $t('Please check and confirm prices.') }}
        </div>
        <region-pricing-table-view v-if="previewData" :data="previewData" />
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import { BCol, BForm, BFormGroup, BRow, BButton, BOverlay } from 'bootstrap-vue';
import DropZone from '@/components/DropZone.vue';
import DatePicker from '@/components/DatePicker.vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper';
import useService from '@/auth/service/useService';
import vSelect from 'vue-select';
import RegionPricingTableView from '@/views/pages/providers/RegionPricingTableView.vue';

export default {
  name: 'CostPriceShipmentInformationForm',
  components: {
    BOverlay,
    BButton,
    BFormGroup,
    BRow,
    BCol,
    BForm,
    DatePicker,
    DropZone,
    ValidationObserver,
    ValidationProvider,
    vSelect,
    RegionPricingTableView
  },

  mixins: [helper],
  props: {
    type: {
      type: String,
      default: 'new'
    },
    values: {
      type: Object,
      default() {
        return {
          file: null,
          packagingType: null,
          providerId: null,
          packageTypeValue: null,
          transportType: null,
          transportDirection: null,
          activeAt: null,
          activeUntil: null
        };
      }
    }
  },

  data() {
    return {
      transportDirectionOptions: [],
      transportTypeOptions: [],
      packagingTypes: [],
      packageTypes: [],
      providers: [],
      transportTypeEnabledProviders: ['PttAbroad'],
      previewedFormData: null,
      previewData: null,
      submitting: false
    };
  },

  computed: {
    showTransportType() {
      if (!this.values.providerId?.text) return false;

      const selectedProvider = this.values.providerId.text;
      return this.transportTypeEnabledProviders.includes(selectedProvider);
    }
  },

  methods: {
    handleFileSelect(files) {
      this.values.file = files[0];
    },

    preview() {
      this.previewedFormData = null;
      this.previewData = null;
      this.$refs.shipmentInfoForm.validate().then(success => {
        if (success) {
          this.submitting = true;

          const formData = {
            ...this.values,
            providerId: this.values.providerId?.value,
            packageType: this.values.packagingType.value,
            packageTypeValue: this.values.packageTypeValue?.value || '',
            transportType: this.values.transportType?.value || '',
            transportDirection: this.values.transportDirection?.value,
            activeAt: this.$dayjs(this.values.activeAt).format('YYYY-MM-DD HH:mm:ss'),
            activeUntil: this.$dayjs(this.values.activeUntil).format('YYYY-MM-DD HH:mm:ss')
          };

          if (!this.showTransportType) {
            formData.transportType = '';
          }

          useService.providerService
            .previewRegionPricing(formData)
            .then(response => {
              this.previewedFormData = response.data;
              this.previewData = response.data?.preview;

              this.$bvModal.show('pricingPreview');
            })
            .catch(messages => {
              if (typeof messages === 'object' && messages && messages.length) {
                messages.forEach(item => {
                  this.showErrorMessage(item.message);
                });
              }
            })
            .finally(() => {
              this.submitting = false;
            });
        }
      });
    },

    submit() {
      if (!this.previewedFormData) return;
      const data = this.previewedFormData;

      this.submitting = true;
      useService.pricingService
        .createPricing(data.id)
        .then(response => {
          this.showSuccessMessage(response.message || '');
          this.$router.push({
            name: 'cost-prices-unit-prices'
          });
        })
        .catch(messages => {
          if (typeof messages === 'object' && messages && messages.length) {
            messages.forEach(item => {
              this.showErrorMessage(item.message);
            });
          }
        })
        .finally(() => {
          this.submitting = false;
        });
    },

    downloadTemplate() {
      if (this.values.providerId?.value) {
        useService.providerService.downloadPricingTemplate(this.values.providerId.value);
      }
    },

    async getPackageTypes() {
      try {
        if (this.values.packagingType?.value === 1) {
          this.packageTypes = await useService.constantService
            .getConsignmentPackageType()
            .then(res => {
              return res.data.map(i => ({
                text: this.$t(i.info),
                value: i.value
              }));
            });
        } else if (this.values.packagingType?.value === 2 && this.values.providerId?.value) {
          this.packageTypes = await useService.providerService
            .getProviderPackageTypes(this.values.providerId.value)
            .then(res => {
              return (res?.data || []).map(i => ({
                text: this.$t(i.description),
                value: i.packagingType
              }));
            });
        } else {
          this.packageTypes = [];
        }
      } catch (error) {
        this.packageTypes = [];
      }
    },

    resetForm() {
      this.$refs.shipmentInfoFormElement.reset();
    }
  },

  watch: {
    'values.providerId': {
      handler() {
        this.values.transportType = null;
        this.values.packageTypeValue = null;
        this.getPackageTypes();
      }
    },
    'values.packagingType': {
      handler() {
        this.values.packageTypeValue = null;
        this.getPackageTypes();
      }
    }
  },

  async created() {
    try {
      const [
        providersResponse,
        packagingTypesResponse,
        transportDirectionResponse,
        transportTypeResponse
      ] = await Promise.all([
        useService.getAllProviders({ paginated: false }).catch(() => ({ data: [] })),
        useService.constantService.getProviderRegionPricePackageType().catch(() => ({ data: [] })),
        useService.constantService.getTransportDirection().catch(() => ({ data: [] })),
        useService.constantService.getTransportType().catch(() => ({ data: [] }))
      ]);

      this.providers = (providersResponse?.data || []).map(provider => ({
        text: provider.name,
        value: provider.id
      }));

      this.packagingTypes = (packagingTypesResponse?.data || []).map(type => ({
        text: this.$t(type.info),
        value: type.value
      }));

      this.transportDirectionOptions = (transportDirectionResponse?.data || []).map(direction => ({
        text: this.$t(direction.info),
        value: direction.value
      }));

      this.transportTypeOptions = (transportTypeResponse?.data || []).map(type => ({
        text: this.$t(type.info),
        value: type.value
      }));
    } catch (error) {
      // console.error('Error fetching initial data:', error);
    }
  },

  setup() {
    const { t } = useI18nUtils();
    return { t };
  }
};
</script>

<style scoped>
.form-group-label {
  margin-bottom: 0.5rem;
}

.download_xls {
  color: #2196f3;
  text-decoration: none;
  cursor: pointer;
}

.download_xls:hover {
  text-decoration: underline;
}
</style>
